/**
 * Bhabhi bot.
 *
 * - Following suit: if it is the last to play the round cannot go wrong, so it sheds its highest card of the suit;
 *   otherwise it plays the highest card that still stays under the best card on the table (a safe way to get rid of
 *   big cards), and only when every card would take the lead does it play its lowest.
 * - Out of the suit (a thulla): dumps its highest card, preferring one that empties a suit.
 * - Leading: its lowest card, preferring suits no opponent has been seen unable to follow.
 * The bot only ever reads its own seat's view.
 */
import { parseCard, type CardId } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { bhabhiLegal, type BhabhiAction, type BhabhiLegal, type BhabhiView } from './engine.js';

const SEATS = 4;
const rank = (c: CardId) => parseCard(c).rank;
const suit = (c: CardId) => parseCard(c).suit;

function chooseCard(view: BhabhiView, legal: BhabhiLegal, ctx: BotContext): CardId {
  const me = view.seat!;
  const cards = legal.cards;
  const byRank = [...cards].sort((a, b) => rank(a) - rank(b));
  const memory = ctx.profile.memory > 0;

  if (view.pile.length === 0) {
    // Leading. Avoid suits an opponent is known to be out of: they could dump on us when we are highest.
    const opponents = [0, 1, 2, 3].filter((s) => s !== me && view.handCounts[s]! > 0);
    const risk = (c: CardId) => (memory ? opponents.filter((s) => view.voids[s]!.includes(suit(c))).length : 0);
    return [...cards].sort((a, b) => risk(a) - risk(b) || rank(a) - rank(b))[0]!;
  }

  if (legal.thulla) {
    // Dump the most awkward card on the player who must take the pile.
    const count = (s: string) => view.hand.filter((c) => suit(c) === s).length;
    const worth = (c: CardId) => rank(c) + (count(suit(c)) === 1 ? 6 : 0);
    return [...cards].sort((a, b) => worth(b) - worth(a))[0]!;
  }

  const best = Math.max(...view.pile.filter((p) => suit(p.card) === view.leadSuit).map((p) => rank(p.card)));
  const played = new Set(view.pile.map((p) => p.seat));
  let after = 0;
  for (let i = 1; i < SEATS; i++) {
    const s = (me + i) % SEATS;
    if (!played.has(s) && view.handCounts[s]! > 0) after++;
  }
  if (after === 0) return byRank.at(-1)!;
  const under = byRank.filter((c) => rank(c) < best);
  return under.length > 0 ? under.at(-1)! : byRank[0]!;
}

export const bhabhiBot: BotEngine<BhabhiView, BhabhiLegal, BhabhiAction> = {
  decideAction(view, legal, ctx) {
    return this.chooseCard!(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    if (legal.cards.length === 0) throw new Error('No legal action');
    if (legal.cards.length === 1) return { type: 'play', card: legal.cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: legal.cards[ctx.rng.int(legal.cards.length)]! };
    return { type: 'play', card: chooseCard(view, legal, ctx) };
  },

  evaluateState(view) {
    if (view.seat === null) return 0;
    const mine = view.handCounts[view.seat]!;
    const others = view.handCounts.filter((_, s) => s !== view.seat);
    const avg = others.reduce((a, b) => a + b, 0) / others.length;
    return Math.max(0, Math.min(1, 0.5 + (avg - mine) / 26));
  },

  calculateRisk(view) {
    return view.seat === null ? 0 : Math.min(1, view.handCounts[view.seat]! / 26);
  },

  handleTimeout(view, legal) {
    const legalNow = legal.cards.length > 0 ? legal : bhabhiLegal(view.hand, view.pile, view.trickNo === 1);
    return { type: 'play', card: [...legalNow.cards].sort((a, b) => rank(a) - rank(b))[0]! };
  },
};
